/**
 * OffsiteSheet — "I'm somewhere else today" (wireframe 1a, right panel).
 * @file src/web/features/clock/components/OffsiteSheet.tsx
 *
 * @llm-rule WHEN: The user is outside every geofence, or chose to clock in from elsewhere
 * @llm-rule NOTE: The map is a schematic, not a tile layer — it shows the fence,
 *   the user, and the gap between them without shipping a mapping dependency.
 */

import { useState } from 'react';
import { Button, Textarea } from '@bloomneo/uikit';
import { ChevronLeft } from 'lucide-react';
import { formatDistance, OFFSITE_REASONS } from '../../../shared/domain';
import { LocationChip, Panel } from '../../../shared/components/presence';

export interface OffsiteSubmission {
  offsiteReason: string;
  offsiteNote: string;
}

export function OffsiteSheet({
  officeName,
  distanceM,
  busy,
  onBack,
  onSubmit,
}: {
  officeName: string | null;
  distanceM: number | null;
  busy: boolean;
  onBack: () => void;
  onSubmit: (submission: OffsiteSubmission) => void;
}) {
  const [reason, setReason] = useState<string>('WFH');
  const [note, setNote] = useState('');

  return (
    <Panel padded={false} className="overflow-hidden">
      <div className="flex items-center justify-between border-b border-rule px-5 py-3">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 text-[12.5px] text-ink-faint hover:text-ink"
        >
          <ChevronLeft className="size-4" />
          back
        </button>
        <span className="text-[12.5px] font-medium text-ink">Off-site clock-in</span>
      </div>

      <div className="flex flex-col gap-3.5 p-5">
        <LocationChip
          inside={false}
          title={`${formatDistance(distanceM)} outside ${officeName ?? 'the office'} zone`}
          detail="Your entry will be marked off-site until it's approved"
        />

        {/* Schematic map: the fence circle, and where you actually are. */}
        <div
          className="relative h-28 overflow-hidden rounded-xl border border-rule"
          style={{
            background:
              'repeating-linear-gradient(0deg, var(--paper-sunk), var(--paper-sunk) 14px, var(--rule) 14px, var(--rule) 15px), repeating-linear-gradient(90deg, transparent, transparent 14px, var(--rule) 14px, var(--rule) 15px)',
          }}
          role="img"
          aria-label={`Schematic showing you ${formatDistance(distanceM)} outside the office geofence`}
        >
          <div
            className="absolute rounded-full border-2 border-dashed"
            style={{ left: 32, top: 30, width: 54, height: 54, borderColor: 'var(--dot-appreciation)' }}
          />
          <div
            className="absolute size-2.5 rounded-full"
            style={{ left: 52, top: 52, background: 'var(--dot-appreciation)' }}
          />
          <div
            className="absolute size-3 rounded-full"
            style={{ right: 44, bottom: 26, background: 'var(--dot-flag)' }}
          />
          <div className="absolute bottom-2 right-3 text-[10px] text-ink-faint">you are here</div>
          <div className="absolute left-3 top-2 text-[10px] text-ink-faint">{officeName}</div>
        </div>

        <div>
          <div className="mb-2 text-[13px] font-semibold text-ink">Why are you off-site?</div>
          <div className="flex flex-wrap gap-2">
            {OFFSITE_REASONS.map((option) => {
              const active = reason === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setReason(option.value)}
                  className={`rounded-full border px-3 py-1.5 text-[12px] font-medium transition-colors ${
                    active
                      ? 'border-ink bg-ink text-paper'
                      : 'border-rule text-ink-soft hover:border-ink-faint'
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Note to your lead…"
          rows={3}
          className="resize-none rounded-xl border-rule text-[12.5px]"
        />

        <p className="rounded-xl border border-dashed border-rule px-3.5 py-2.5 text-[11.5px] leading-relaxed text-ink-soft">
          Notifies your <b className="font-semibold">manager</b>, and the department head above
          them. The entry stays marked <b className="font-semibold">off-site</b> until one of them
          approves it.
        </p>

        <Button
          className="h-12 rounded-[14px] bg-ink text-[15px] font-semibold text-paper hover:bg-ink/90"
          disabled={busy}
          onClick={() => onSubmit({ offsiteReason: reason, offsiteNote: note })}
        >
          {busy ? 'Submitting…' : 'Scan face & submit'}
        </Button>
      </div>
    </Panel>
  );
}
